import { navigate } from "@reach/router";
import React, { createContext, useContext, useReducer } from "react";
import VideosReducer from "../reducers/VideosReducer";
import AdjuntosService from "../services/AdjuntosService";
import ProgramsService from "../services/ProgramsService";
import TagsService from "../services/TagsService";
import VideosService from "../services/VideosService";
import {
  VIDEOS_RECEIVED,
  SINGLE_VIDEO_RECEIVED,
  AGREGAR_FAVORITO,
  ELIMINAR_FAVORITO,
  SET_PROPERTY_VIDEO,
  CREATE_VIDEO,
  PURCHASE_NEEDED,
  SHOW_SPINNER,
  HIDE_SPINNER,
  TAGS_RECEIVED,
  PROGRAMS_RECEIVED,
  SET_COMPLETED,
  CLEAR_RELOAD,
  SET_NONCE,
} from "../types";
import { hideModal, randomString } from "../utils";
import { ModalContext } from "./ModalContext";

const initialState = {
  favoritos: null,
  programs: null,
  videos: null,
  video: null,
  nonce: null,
  spinner: false,
  reloadVideos: false,
};

export const VideosContext = createContext(initialState);

export const VideosProvider = ({ children }) => {
  const [state, dispatch] = useReducer(VideosReducer, initialState);

  const { alert, success } = useContext(ModalContext);

  const getTags = () => {
    dispatch({ type: TAGS_RECEIVED, payload: null });
    TagsService.getTags().then((res) => {
      const { tags } = res.data;
      dispatch({ type: TAGS_RECEIVED, payload: tags });
    });
  };
  const getPrograms = () => {
    ProgramsService.getPrograms().then((res) => {
      const { programs } = res.data;
      dispatch({ type: PROGRAMS_RECEIVED, payload: programs });
    });
  };

  const getVideos = (filters) => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    dispatch({ type: SHOW_SPINNER });
    VideosService.getVideos(filters)
      .then((res) => {
        const { videos } = res.data;
        dispatch({ type: HIDE_SPINNER });
        dispatch({ type: VIDEOS_RECEIVED, payload: { videos, nonce } });
      })
      .catch((error) => {
        dispatch({ type: HIDE_SPINNER });
        if (error.response) {
          if (error.response.status === 412) {
            dispatch({ type: PURCHASE_NEEDED });
          }
        }
      });
  };

  const getVideosByProgram = (url, filters) => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    VideosService.getVideosByProgram(url, filters)
      .then((res) => {
        const { videos } = res.data;
        dispatch({ type: VIDEOS_RECEIVED, payload: { videos, nonce } });
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 412) {
            dispatch({ type: PURCHASE_NEEDED });
          }
        }
      });
  };

  const getVideosByTag = (tag_id, filters) => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    VideosService.getVideosByTag(tag_id, filters)
      .then((res) => {
        const { videos } = res.data;
        dispatch({ type: VIDEOS_RECEIVED, payload: { videos, nonce } });
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 412) {
            dispatch({ type: PURCHASE_NEEDED });
          }
        }
      });
  };

  const getFavoritos = (filters) => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    VideosService.getFavoritos(filters).then((res) => {
      const { favorites } = res.data;
      dispatch({
        type: VIDEOS_RECEIVED,
        payload: { videos: favorites, nonce },
      });
    });
  };

  const clearFavoritos = () => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    dispatch({ type: VIDEOS_RECEIVED, payload: { videos: null, nonce } });
  };

  const getAllVideos = (query) => {
    let nonce = randomString(16);
    dispatch({ type: SET_NONCE, payload: nonce });
    VideosService.getAllVideos(query).then((res) => {
      const { videos } = res.data;
      dispatch({ type: VIDEOS_RECEIVED, payload: { videos, nonce } });
    });
  };

  const getSingleVideo = (video_id) => {
    VideosService.getSingleVideo(video_id)
      .then((res) => {
        const { video } = res.data;
        dispatch({ type: SINGLE_VIDEO_RECEIVED, payload: video });
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.status === 404) {
            alert("Lo sentimos, no hemos encontrado este video.");
          }
        }
      });
  };

  const clearReload = () => {
    dispatch({ type: CLEAR_RELOAD });
  };

  const agregarFavorito = (video_id) => {
    VideosService.postFavorito(video_id).then(() => {
      dispatch({ type: AGREGAR_FAVORITO, payload: video_id });
      success("¡Favorito agregado!");
      getSingleVideo(video_id);
    });
  };

  const eliminarFavorito = (video_id) => {
    VideosService.deleteFavorito(video_id).then(() => {
      dispatch({ type: ELIMINAR_FAVORITO, payload: video_id });
      success("¡Favorito eliminado!");
      getSingleVideo(video_id);
    });
  };

  const deleteVideo = (video_id) => {
    VideosService.deleteVideo(video_id).then(() => {
      navigate("/mytbm/admin/videos");
      success("¡Video eliminado con éxito!");
      hideModal();
      getAllVideos();
    });
  };

  const setPropiedadVideo = (key, value) => {
    dispatch({ type: SET_PROPERTY_VIDEO, payload: { key, value } });
  };

  const clearVideos = () => {
    const nonce = null;
    dispatch({ type: SET_NONCE, payload: nonce });
    dispatch({
      type: VIDEOS_RECEIVED,
      payload: { videos: null, nonce },
    });
  };

  const createVideo = () => {
    dispatch({ type: CREATE_VIDEO });
  };

  const postVideo = (video) => {
    const callback = () => {
      dispatch({ type: HIDE_SPINNER });
      navigate("/mytbm/admin/videos");
      success("Video guardado con éxito");
    };
    const handleError = () => {
      dispatch({ type: HIDE_SPINNER });
      alert("Hubo un error al subir el video. Vuelve a intentarlo.");
    };
    if (video.file && video.file !== null) {
      const formData = new FormData();
      formData.append("adjunto", video.file);
      dispatch({ type: SHOW_SPINNER });
      AdjuntosService.postAdjunto(formData)
        .then((res) => {
          const { idAdjunto } = res.data;
          video.thumbnail = idAdjunto;
          if (isNaN(video.video_id)) {
            VideosService.postVideo(video).then(callback).catch(handleError);
          } else {
            VideosService.putVideo(video).then(callback).catch(handleError);
          }
        })
        .catch((error) => {
          dispatch({ type: HIDE_SPINNER });
        });
    } else {
      dispatch({ type: SHOW_SPINNER });
      if (isNaN(video.video_id)) {
        VideosService.postVideo(video).then(callback).catch(handleError);
      } else {
        VideosService.putVideo(video).then(callback).catch(handleError);
      }
    }
  };

  const completarVideo = (video_id) => {
    VideosService.completarVideo(video_id).then(() => {
      success("Video marcado como completado.");
      getSingleVideo(video_id);
    });
  };

  const eliminarCompletado = (video_id) => {
    VideosService.eliminarCompletado(video_id).then(() => {
      success("Video marcado como incompleto.");
      getSingleVideo(video_id);
    });
  };

  const setCompleted = (completed) => {
    dispatch({ type: SET_COMPLETED, payload: completed });
  };

  return (
    <VideosContext.Provider
      value={{
        ...state,
        getTags,
        postVideo,
        getVideos,
        clearReload,
        setCompleted,
        getPrograms,
        createVideo,
        clearVideos,
        deleteVideo,
        getAllVideos,
        getFavoritos,
        getVideosByTag,
        clearFavoritos,
        getSingleVideo,
        agregarFavorito,
        completarVideo,
        eliminarCompletado,
        eliminarFavorito,
        setPropiedadVideo,
        getVideosByProgram,
      }}
    >
      {children}
    </VideosContext.Provider>
  );
};
